import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Icon, Button } from '../../components/ui';

pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// === Constantes politique TTN ===
const TTN_POLICY = {
  oid: 'urn:2.16.788.1.2.1.3',
  title: 'Politique de Signature Electronique de Tunisie TradeNet',
  url: 'https://www.tradenet.com.tn/Politique_Signature_Electronique_Tunisie_TradeNet.pdf',
};

const PREDEFINED_ROLES = [
  'Gérant',
  'Directeur Général',
  'Directeur Financier',
  'Comptable',
  'Comptable Agréé',
  'Expert-Comptable',
  'Représentant Légal',
  'Administrateur',
];

const LAST_ROLE_STORAGE_KEY = 'lastSignerRole';
const AGENT_URL = 'http://localhost:5050';

// === Helper : télécharger un certificat (base64 DER) ===
function downloadCert(derBase64, baseName, format) {
  let blob, fileName;
  if (format === 'pem') {
    // Conversion en PEM
    const pem = '-----BEGIN CERTIFICATE-----\n' +
                derBase64.match(/.{1,64}/g).join('\n') +
                '\n-----END CERTIFICATE-----\n';
    blob = new Blob([pem], { type: 'application/x-pem-file' });
    fileName = baseName + '.pem';
  } else {
    // Format DER (.cer)
    const binary = atob(derBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    blob = new Blob([bytes], { type: 'application/x-x509-ca-cert' });
    fileName = baseName + '.cer';
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const SignatureWorkflowModal = ({ isOpen, onClose, onConfirm, facture, apiUrl }) => {
  const [step, setStep] = useState(1);

  // === Étape 1 (PDF) ===
  const [pdfBlob, setPdfBlob] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [renderedPages, setRenderedPages] = useState(new Set());
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [error, setError] = useState(null);
  const [canProceed, setCanProceed] = useState(false);
  const [pageWidth, setPageWidth] = useState(800);

  // === Étape 2 (Rôle + Consentement) ===
  const [roleSelected, setRoleSelected] = useState('');
  const [roleCustom, setRoleCustom] = useState('');
  const [consented, setConsented] = useState(false);

  // === Étape 3 (Certificats) ===
  const [certificates, setCertificates] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [certsError, setCertsError] = useState(null);
  const [selectedCertId, setSelectedCertId] = useState(null);

  const containerRef = useRef(null);
  const sentinelRef = useRef(null);

  // === Reset à chaque ouverture ===
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setPdfBlob(null);
      setNumPages(0);
      setRenderedPages(new Set());
      setCanProceed(false);
      setError(null);
      setLoadingPdf(true);
      setConsented(false);
      setCertificates([]);
      setLoadingCerts(false);
      setCertsError(null);
      setSelectedCertId(null);

      try {
        const lastRole = localStorage.getItem(LAST_ROLE_STORAGE_KEY);
        if (lastRole) {
          if (PREDEFINED_ROLES.includes(lastRole)) {
            setRoleSelected(lastRole);
            setRoleCustom('');
          } else {
            setRoleSelected('Autre');
            setRoleCustom(lastRole);
          }
        } else {
          setRoleSelected('');
          setRoleCustom('');
        }
      } catch (e) {
        setRoleSelected('');
        setRoleCustom('');
      }
    }
  }, [isOpen]);

  // === PDF fetch ===
  useEffect(() => {
    if (!isOpen || !facture) return;
    let cancelled = false;

    const fetchPdf = async () => {
      try {
        const invoiceId = facture.id || facture._id;
        const token = localStorage.getItem('token');
        const workspaceId = localStorage.getItem('active_workspace_id');
        const headers = { 'Authorization': `Bearer ${token}` };
        if (workspaceId) headers['x-workspace-id'] = workspaceId;

        const response = await fetch(`${apiUrl}/api/invoices/${invoiceId}/pdf`, {
          method: 'GET',
          headers,
        });
        if (!response.ok) throw new Error('Erreur lors du chargement du PDF.');
        const blob = await response.blob();
        if (!cancelled) setPdfBlob(blob);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Impossible de charger le PDF.');
      } finally {
        if (!cancelled) setLoadingPdf(false);
      }
    };
    fetchPdf();
    return () => { cancelled = true; };
  }, [isOpen, facture, apiUrl]);

  // === Largeur PDF responsive ===
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        setPageWidth(Math.min(900, Math.max(320, w - 48)));
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [pdfBlob, step]);

  // === IntersectionObserver pour le scroll ===
  const allRendered = numPages > 0 && renderedPages.size >= numPages;
  useEffect(() => {
    if (step !== 1) return;
    if (!allRendered || !sentinelRef.current || canProceed) return;

    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setCanProceed(true); },
      { root: containerRef.current, threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [step, allRendered, canProceed]);

  // === Charger les certificats du token au passage à l'étape 3 ===
  useEffect(() => {
    if (step !== 3 || certificates.length > 0 || loadingCerts) return;

    setLoadingCerts(true);
    setCertsError(null);

    fetch(`${AGENT_URL}/list-certificates`)
      .then(async (resp) => {
        if (!resp.ok) {
          const errData = await resp.json().catch(() => null);
          throw new Error(errData?.error || `HTTP ${resp.status}`);
        }
        return resp.json();
      })
      .then((data) => {
        if (!data.success || !data.certificates) {
          throw new Error(data.error || 'Réponse invalide de l\'agent');
        }
        setCertificates(data.certificates);
        // Pré-sélectionner le premier par défaut
        if (data.certificates.length > 0) {
          setSelectedCertId(data.certificates[0].id);
        }
      })
      .catch((err) => {
        setCertsError(
          err.message.includes('Failed to fetch')
            ? "Impossible de communiquer avec la clé USB. Vérifiez que l'agent tourne sur le port 5050 et que votre clé est insérée."
            : err.message
        );
      })
      .finally(() => setLoadingCerts(false));
  }, [step, certificates.length, loadingCerts]);

  if (!isOpen) return null;

  const progressPercent = numPages > 0 ? Math.round((renderedPages.size / numPages) * 100) : 0;
  const effectiveRole = roleSelected === 'Autre' ? roleCustom.trim() : roleSelected;
  const canValidateRoleConsent = effectiveRole.length >= 2 && consented;
  const canFinalSign = selectedCertId != null && certificates.length > 0;

  // === Handler signature finale ===
  const handleFinalSign = () => {
    try {
      localStorage.setItem(LAST_ROLE_STORAGE_KEY, effectiveRole);
    } catch (e) {}

    const chosenCert = certificates.find(c => c.id === selectedCertId);

    onConfirm({
      role: effectiveRole,
      consented: true,
      policy: TTN_POLICY,
      certificateId: selectedCertId,
      certificateInfo: chosenCert ? {
        label: chosenCert.label,
        subjectCN: chosenCert.subjectCN,
        issuerCN: chosenCert.issuerCN,
      } : null,
    });
  };

  // === Composant : étape du stepper ===
  const StepperBubble = ({ n, label, active, done }) => (
    <div className={`flex items-center gap-1 text-xs ${(active || done) ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-xs ${(active || done) ? 'bg-blue-600' : 'bg-gray-300'}`}>
        {done ? <Icon name="IconCheck" className="text-[10px]" /> : n}
      </span>
      {label}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '90vh' }}>

        {/* === Header === */}
        <div className="flex justify-between items-start px-6 py-4 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-800">Signature de la facture</h2>
            <p className="text-sm text-gray-500 mt-1">
              N° {facture?.number}
              {step === 1 && ' — Étape 1/3 : Lecture'}
              {step === 2 && ' — Étape 2/3 : Identité & consentement'}
              {step === 3 && ' — Étape 3/3 : Sélection du certificat'}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <StepperBubble n={1} label="Lecture" active={step === 1} done={step > 1} />
              <div className={`h-0.5 w-6 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`} />
              <StepperBubble n={2} label="Consentement" active={step === 2} done={step > 2} />
              <div className={`h-0.5 w-6 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`} />
              <StepperBubble n={3} label="Certificat" active={step === 3} done={false} />
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-3xl leading-none ml-4" aria-label="Fermer">×</button>
        </div>

        {/* === ÉTAPE 1 : PDF === */}
        {step === 1 && (
          <>
            {numPages > 0 && (
              <div className="px-6 pt-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Pages rendues : {renderedPages.size} / {numPages}</span>
                  <span>{progressPercent}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            )}

            <div ref={containerRef} className="flex-1 overflow-y-auto bg-gray-100 p-6" style={{ minHeight: 400 }}>
              {loadingPdf && (
                <div className="flex justify-center items-center h-64 text-gray-500">
                  <Icon name="IconSpinner" className="animate-spin text-3xl mr-3" />
                  <span>Chargement de la facture...</span>
                </div>
              )}
              {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-lg">
                  <Icon name="IconAlertTriangle" className="mr-2" />{error}
                </div>
              )}
              {pdfBlob && !error && (
                <div className="flex flex-col items-center">
                  <Document
                    file={pdfBlob}
                    onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                    onLoadError={(err) => setError(err?.message || 'Erreur de chargement PDF')}
                    loading={<div className="text-gray-500 py-8">Chargement du PDF...</div>}
                  >
                    {Array.from({ length: numPages }, (_, i) => (
                      <div key={i} className="mb-4 shadow-md bg-white">
                        <Page
                          pageNumber={i + 1}
                          width={pageWidth}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          onRenderSuccess={() => setRenderedPages((prev) => {
                            const next = new Set(prev);
                            next.add(i + 1);
                            return next;
                          })}
                        />
                      </div>
                    ))}
                    <div ref={sentinelRef} style={{ height: 1, width: '100%' }} aria-hidden="true" />
                  </Document>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="text-sm flex items-center min-h-[1.5rem]">
                  {canProceed ? (
                    <span className="text-green-600 font-medium"><Icon name="IconCheckCircle" className="mr-2" />Document parcouru</span>
                  ) : allRendered ? (
                    <span className="text-orange-600"><Icon name="IconArrowDown" className="mr-2 animate-bounce" />Faites défiler jusqu'à la fin</span>
                  ) : (
                    <span className="text-gray-500"><Icon name="IconHourglass" className="mr-2" />Rendu en cours...</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">Annuler</button>
                  <button
                    onClick={() => setStep(2)}
                    disabled={!canProceed}
                    className={`px-6 py-2 rounded-lg text-white font-medium transition-colors flex items-center ${canProceed ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}
                  >
                    Continuer<Icon name="IconArrowRight" className="ml-2" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* === ÉTAPE 2 : RÔLE + CONSENTEMENT === */}
        {step === 2 && (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6" style={{ minHeight: 400 }}>

              <section>
                <h3 className="text-base font-semibold text-gray-800 mb-2">
                  <Icon name="IconUserTie" className="text-blue-600 mr-2" />
                  Votre rôle dans la signature
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Indiquez la qualité avec laquelle vous signez cette facture.
                </p>
                <select
                  value={roleSelected}
                  onChange={(e) => setRoleSelected(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Sélectionnez un rôle —</option>
                  {PREDEFINED_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  <option value="Autre">Autre (à préciser)</option>
                </select>
                {roleSelected === 'Autre' && (
                  <input
                    type="text"
                    value={roleCustom}
                    onChange={(e) => setRoleCustom(e.target.value)}
                    placeholder="Précisez votre rôle"
                    maxLength={80}
                    className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </section>

              <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-base font-semibold text-gray-800 mb-2">
                  <Icon name="IconFileContract" className="text-blue-600 mr-2" />
                  Politique de signature Tunisie TradeNet
                </h3>
                <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                  En signant cette facture, vous vous engagez à respecter la politique officielle de signature
                  électronique définie par <strong>Tunisie TradeNet (TTN)</strong>.
                </p>
                <div className="text-xs text-gray-600 mb-3 space-y-1">
                  <div><strong>Identifiant :</strong> {TTN_POLICY.oid}</div>
                  <div><strong>Titre :</strong> {TTN_POLICY.title}</div>
                </div>
                <a href={TTN_POLICY.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium">
                  <Icon name="IconExternalLink" className="mr-2" />Consulter le document officiel (PDF)
                </a>
                <label className="flex items-start mt-4 cursor-pointer bg-white border border-gray-300 rounded-lg p-3 hover:bg-gray-50">
                  <input type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)} className="mt-1 mr-3 h-4 w-4" />
                  <span className="text-sm text-gray-800">
                    <strong>Je consens</strong> à signer cette facture conformément à la
                    Politique de Signature Electronique de Tunisie TradeNet.
                  </span>
                </label>
              </section>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="text-sm">
                  {canValidateRoleConsent ? (
                    <span className="text-green-600 font-medium"><Icon name="IconCheckCircle" className="mr-2" />Prêt à choisir le certificat</span>
                  ) : (
                    <span className="text-orange-600"><Icon name="IconExclamationCircle" className="mr-2" />Rôle + consentement requis</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep(1)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"><Icon name="IconArrowLeft" className="mr-2" />Retour</button>
                  <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">Annuler</button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!canValidateRoleConsent}
                    className={`px-6 py-2 rounded-lg text-white font-medium transition-colors flex items-center ${canValidateRoleConsent ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}
                  >
                    Continuer<Icon name="IconArrowRight" className="ml-2" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* === ÉTAPE 3 : SÉLECTION CERTIFICAT === */}
        {step === 3 && (
          <>
            <div className="flex-1 overflow-y-auto p-6" style={{ minHeight: 400 }}>
              <section className="mb-4">
                <h3 className="text-base font-semibold text-gray-800 mb-1">
                  <Icon name="IconIdCard" className="text-blue-600 mr-2" />
                  Certificats disponibles sur votre clé USB
                </h3>
                <p className="text-sm text-gray-600">
                  Choisissez le certificat que vous souhaitez utiliser pour signer cette facture.
                  Vous pouvez aussi télécharger chacun d'eux pour consultation.
                </p>
              </section>

              {loadingCerts && (
                <div className="flex justify-center items-center py-12 text-gray-500">
                  <Icon name="IconSpinner" className="animate-spin text-3xl mr-3" />
                  <span>Lecture du magasin de clés...</span>
                </div>
              )}

              {certsError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg">
                  <div className="font-medium mb-1">
                    <Icon name="IconAlertTriangle" className="mr-2" />
                    Erreur d'accès au token
                  </div>
                  <div className="text-sm">{certsError}</div>
                  <button
                    onClick={() => { setCertificates([]); setCertsError(null); }}
                    className="mt-3 text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                  >
                    <Icon name="IconRefresh" className="mr-1" />Réessayer
                  </button>
                </div>
              )}

              {!loadingCerts && !certsError && certificates.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  Aucun certificat trouvé sur le token.
                </div>
              )}

              {!loadingCerts && !certsError && certificates.length > 0 && (
                <div className="space-y-3">
                  {certificates.map((cert) => {
                    const isSelected = selectedCertId === cert.id;
                    const isExpired = new Date(cert.validTo) < new Date();
                    return (
                      <label
                        key={cert.id}
                        className={`block cursor-pointer rounded-lg border-2 p-4 transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name="certificate"
                            value={cert.id}
                            checked={isSelected}
                            onChange={() => setSelectedCertId(cert.id)}
                            className="mt-1 h-4 w-4 text-blue-600"
                            disabled={isExpired}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-semibold text-gray-800">{cert.subjectCN || cert.label}</span>
                              {isExpired && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Expiré</span>}
                              {!isExpired && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Valide</span>}
                            </div>
                            <div className="text-xs text-gray-600 space-y-0.5">
                              {cert.label && cert.label !== cert.subjectCN && (
                                <div><strong>Label :</strong> {cert.label}</div>
                              )}
                              <div><strong>Émetteur :</strong> {cert.issuerCN}</div>
                              <div>
                                <strong>Validité :</strong> du {new Date(cert.validFrom).toLocaleDateString('fr-FR')} au{' '}
                                {new Date(cert.validTo).toLocaleDateString('fr-FR')}
                              </div>
                              {cert.serialNumber && (
                                <div className="font-mono text-[10px] truncate"><strong>N° série :</strong> {cert.serialNumber}</div>
                              )}
                            </div>
                            <div className="mt-3 flex gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  downloadCert(cert.derBase64, cert.label || cert.subjectCN || 'certificate', 'cer');
                                }}
                                className="text-xs px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                              >
                                <Icon name="IconDownload" className="mr-1" />Télécharger (.cer)
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  downloadCert(cert.derBase64, cert.label || cert.subjectCN || 'certificate', 'pem');
                                }}
                                className="text-xs px-3 py-1 rounded bg-gray-600 text-white hover:bg-gray-700"
                              >
                                <Icon name="IconDownload" className="mr-1" />Télécharger (.pem)
                              </button>
                            </div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="text-sm">
                  {canFinalSign ? (
                    <span className="text-green-600 font-medium">
                      <Icon name="IconCheckCircle" className="mr-2" />
                      Prêt à signer avec le certificat sélectionné
                    </span>
                  ) : (
                    <span className="text-orange-600">
                      <Icon name="IconExclamationCircle" className="mr-2" />
                      Sélectionnez un certificat pour continuer
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep(2)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">
                    <Icon name="IconArrowLeft" className="mr-2" />Retour
                  </button>
                  <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">Annuler</button>
                  <button
                    onClick={handleFinalSign}
                    disabled={!canFinalSign}
                    className={`px-6 py-2 rounded-lg text-white font-medium transition-colors flex items-center ${canFinalSign ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}
                  >
                    <Icon name="IconSignature" className="mr-2" />Signer
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

SignatureWorkflowModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  facture: PropTypes.object,
  apiUrl: PropTypes.string.isRequired,
};

export default SignatureWorkflowModal;
